import React, { useState } from "react";

import { Modal, Select, TextArea } from "@king-ict/eupisi-ui/components";
import { SelectEvent } from "@king-ict/eupisi-ui/components/Select/SelectEvent";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import {
  ManualStatusChangeRequest,
  ManualStatusTransitionOption,
  ManualStatusChangeValidationScheme,
  createManualStatusChangeRequest,
} from "../../models/ProgramRequest";

export interface ManualStatusChangeModalProps {
  programRequestId: number;
  allowedStatusTransitions: ManualStatusTransitionOption[];
  onConfirm: (request: ManualStatusChangeRequest) => void;
  onClose: (event: React.MouseEvent<any, MouseEvent>) => void;
}

export const ManualStatusChangeModal = ({
  programRequestId,
  allowedStatusTransitions,
  onConfirm,
  onClose,
}: ManualStatusChangeModalProps) => {
  const { t } = useTranslation();

  const [request, setRequest] = useState<ManualStatusChangeRequest>(
    createManualStatusChangeRequest(programRequestId)
  );

  const [errors, doValidations] = useValidation({
    data: request,
    validationSchema: ManualStatusChangeValidationScheme,
  });

  const handleStatusChange = (e: React.ChangeEvent<SelectEvent>) => {
    const value = e.target.value;
    setRequest({
      ...request,
      newStatusId: value === "" || value === undefined || value === null ? undefined : Number(value),
      sourceStatusHistoryId: allowedStatusTransitions.find((option) => option.id === Number(value))
        ?.sourceStatusHistoryId,
    });
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRequest({ ...request, description: e.target.value });
  };

  const handleConfirm = () => {
    const isValid = doValidations();
    if (!isValid) return;

    onConfirm({ ...request, description: request.description.trim() });
  };

  return (
    <Modal
      type="confirm"
      variant="big"
      title={t("manualStatusChange.modalTitle")}
      cancelText={t("common.cancel")}
      confirmText={t("manualStatusChange.confirmButton")}
      open
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <div>
        <Row>
          <Col md={12}>
            <Select
              label={t("manualStatusChange.selectStatusLabel")}
              name={nameof<typeof request>("newStatusId")}
              value={request.newStatusId}
              onChange={handleStatusChange}
              selectData={codeBookToSelectData(allowedStatusTransitions)}
              emptyOption={{ text: "", value: undefined }}
              errorText={errors?.newStatusId}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextArea
              label={t("manualStatusChange.noteLabel")}
              ariaLabel={t("manualStatusChange.noteLabel")}
              name={nameof<typeof request>("description")}
              value={request.description}
              onChange={handleNoteChange}
              maxLength={3600}
              rows={6}
              errorText={errors?.description}
            />
          </Col>
        </Row>
      </div>
    </Modal>
  );
};
